import dayjs from "dayjs";
import { FC, useRef } from "react";
import ObservationFormPreview from "src/components/ObservationFormPreview/ObservationFormPreview";
import InstanceReportSummary from "src/features/reporting/components/InstanceReportSummary/InstanceReportSummary";
import { useFormInstance } from "src/hooks/useObservationForm";
import formatInstance from "src/utils/formatInstance";
import GradingForm from "src/features/reporting/components/GradingForm/GradingForm";

export interface AdminObservationSlideProps {
	formInstanceId: number;
	form?: Partial<IObservationForm>;
	showInstanceSummary?: boolean;
	handlecloseReportAnswerModalClick?: () => void;
	showGradingReportState?: boolean;
}

const AdminObservationSlide: FC<AdminObservationSlideProps> = ({
	formInstanceId,
	form,
	showInstanceSummary = false,
	handlecloseReportAnswerModalClick,
	showGradingReportState
}) => {
	const { data, isLoading: isLoadingFormInstance } =
		useFormInstance(formInstanceId);

	const gradingHeadingRef = useRef<HTMLDivElement>(null);

	const formInstance = formatInstance(data);

	const isGCObservation = form?.observation_type === "GREAT_CONVERSATION";

	let GCQuestionCount = undefined;
	let GCQuestionCorrectCount = undefined;

	if (isGCObservation && formInstance) {
		GCQuestionCorrectCount = formInstance.response.filter(
			({ question }) => question.isGreatConversationCorrect
		).length;

		GCQuestionCount = formInstance.response.filter(
			({ question }) => question.isCorrectAnswerRequired
		).length;
	}

	const showGradingReport =
		formInstance && isGCObservation && showGradingReportState;

	const observationDate = formInstance?.observationinstance_response.find(
		(resp) => resp.observationquestion.question_type === "OBSERVATION_DATE"
	)?.value;

	if (isLoadingFormInstance) {
		return (
			<div className="flex justify-center items-center">
				<div className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin" />
			</div>
		);
	}

	return (
		<div data-testid="AdminObservationSlide">
			{showInstanceSummary && formInstance && form && (
				<InstanceReportSummary
					completed={formInstance.complete}
					compliant={formInstance.compliant}
					createdBy={formInstance.created_by}
					passedThreshold={formInstance.score_threshold_passed}
					completedAt={dayjs(formInstance.completed_at).format(
						"DD/MM/YYYY, HH:mm"
					)}
					observedAt={
						observationDate
							? dayjs(observationDate).format("DD/MM/YYYY")
							: undefined
					}
					storeId={formInstance.store}
					userId={formInstance.created_for}
					observationType={form.observation_type}
					overallScore={formInstance.overall_score}
					overallScoreBreakdown={
						isGCObservation
							? `${GCQuestionCorrectCount} / ${GCQuestionCount}`
							: undefined
					}
					greatConversationGradePerformance={
						formInstance.great_conversation_grade_performance
					}
					graded={formInstance.graded}
					showGradingReport={showGradingReportState}
					gradingHeadingRef={gradingHeadingRef}
					month={formInstance.month ? formInstance.month - 1 : undefined}
				/>
			)}

			{formInstance && (
				<ObservationFormPreview
					questions={formInstance.response.map(({ question }) => question)}
					formName={form?.name}
					className="mt-6"
				/>
			)}

			{showGradingReport && (
				<>
					<hr className="my-10 border-border" />

					<h2 className="text-xl font-semibold mb-8" ref={gradingHeadingRef}>
						Grading
					</h2>

					<GradingForm
						formInstanceId={formInstanceId}
						gradePerformance={formInstance.great_conversation_grade_performance}
						gradingComments={formInstance.great_conversation_grading_comments}
						graded={formInstance.graded}
						handlecloseReportAnswerModalClick={
							handlecloseReportAnswerModalClick
						}
					/>
				</>
			)}
		</div>
	);
};

export default AdminObservationSlide;
